import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "react-bootstrap";

import DateFilter from "@cleanup/components/AdvancedFilters/DateFilter/DateFilter";
import StringFilter from "@cleanup/components/AdvancedFilters/StringFilter/StringFilter";
import NumericFilter from "@cleanup/components/AdvancedFilters/NumericFilter/NumericFilter";
import BooleanFilter from "@cleanup/components/AdvancedFilters/BooleanFilter/BooleanFilter";
import MultiStringFilter from "@cleanup/components/AdvancedFilters/MultiStringFilter/MultiStringFilter";
import MultiCheckboxFilter from "@cleanup/components/AdvancedFilters/MultiCheckboxFilter/MultiCheckboxFilter";
import MultiSelectAutocompleteFilter from "@cleanup/components/AdvancedFilters/MultiSelectAutocompleteFilter/MultiSelectAutocompleteFilter";
import ConfigureFiltersModal from "@cleanup/components/AdvancedFilters/ConfigureFiltersModal/ConfigureFiltersModal";
import SntFilterSettingIcon from "@icons/SntFilterSettingIcon";
import SntLink from "@/components/ReactBootstrap/SntLink";
import { AdvanceFilter } from "@cleanup/components/AdvancedFilters/FilterStyle";
import { SntCollapse } from "@cleanup/widgets/SntCollapse";
import { ActionEnum } from "@/components/SntTableViewCard/ActionEnum";
import momentTZ from "@/constants/SensolusMoment";
import BoundFilter from "@cleanup/components/AdvancedFilters/BoundFilter/BoundFilter";
import ActionTool from "@/containers/AdminLayout/Toolbar/ActionTool";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { translate } from "@/hooks/useLanguage";
import usageAnalyticsClient from "@/apis/usageAnalyticsClient";

interface FilterTypeSettings {
  [key: string]: unknown;
}

interface SearchField {
  _idx: string;
  key: string;
  label?: string;
  description: string;
  filterType: string;
  filterTypeSettings: FilterTypeSettings;
  [key: string]: unknown;
}

interface CategoryDescriptor {
  categoryID: string;
  categoryLabel: string;
  searchFields: SearchField[];
}

interface FilterParam {
  filterKey: string;
  filterType: string;
  categoryId?: string;
  searchType?: string;
  filterValue?: Record<string, unknown>;
  notFilter?: boolean;
  [key: string]: unknown;
}

interface BasicQuery {
  searchQueryType?: string;
  query: FilterParam[];
}

interface Action {
  type: string;
  idx: string | null;
}

interface BucketRange {
  __bucketRange: true;
  min: number | string | null;
  max: number | string | null;
}

interface ForceFilterAction {
  filterKeys: string[];
  action: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  query?: any[];
}

interface ActionToolOption {
  id: string;
  type?: string;
  title?: string;
  icon?: React.ReactNode;
  link?: string;
  fullLinkInNewTab?: string;
  customActiveClass?: string;
  isHideText?: boolean;
  isAlwayShow?: boolean;
}

interface FilterListProps {
  isVisibleAddFilterBtn?: boolean;
  restrictedFilters?: string[];
  onChange?: (basicQuery: BasicQuery, action: Action) => void;
  data: {
    descriptors: CategoryDescriptor[];
    appliedFilterSettings?: FilterParam[];
    filtersVisibleAtInit?: string[];
    defaultFilters?: Record<string, Record<string, unknown>>;
  };
  onResetFilterData?: () => void;
  filterListRef?: React.MutableRefObject<{ clearFilter: () => void } | undefined>;
  disabled?: boolean;
  totalElement?: string | number;
  filterToolList?: ActionToolOption[];
  handleTool?: (tool: unknown) => void;
  deviceCategorySuggestion?: string;
  forceFilterAction?: ForceFilterAction | null;
  setForceFilterAction?: (action: ForceFilterAction | null) => void;
  isDisableCollapseOnMobile?: boolean;
  /**
   * The organisation the filters below describe, handed to every child that
   * fetches suggestions. This component reads no organisation of its own — the
   * page that renders it knows which one applies (see MultiCheckboxFilter.orgId).
   */
  orgId?: number | null;
}

interface ComponentData {
  descriptor: SearchField;
  data: FilterParam;
}

const FilterList: React.FC<FilterListProps> = ({
  isVisibleAddFilterBtn = true,
  restrictedFilters = [],
  onChange,
  data = { descriptors: [], appliedFilterSettings: [], filtersVisibleAtInit: [] },
  onResetFilterData,
  filterListRef,
  disabled = false,
  totalElement,
  filterToolList,
  handleTool,
  deviceCategorySuggestion,
  orgId = null,
  forceFilterAction,
  setForceFilterAction,
  isDisableCollapseOnMobile,
}) => {
  const [filtersVisible, setFiltersVisible] = useState<string[]>([]);
  const [components, setComponents] = useState<ComponentData[]>([]);
  const [isModalShow, setModalShow] = useState(false);

  const action = useRef<Action>({ type: ActionEnum.EMPTY, idx: null });
  const lastChangedFilter = useRef<string | null>(null);
  const mapDescriptors = useRef<Record<string, SearchField>>({});
  const appliedFilterSettings = useRef<Record<string, FilterParam>>({});
  const previousComponentRender = useRef<JSX.Element[]>([]);
  const filterRef = useRef<HTMLAnchorElement>(null);

  const _getDescriptorByFilter = (_filter: FilterParam): SearchField | null => {
    for (const _idx in mapDescriptors.current) {
      const _descriptor = mapDescriptors.current[_idx];

      if (
        (_descriptor.key === _filter.key ||
          _descriptor.key === _filter.filterKey) &&
        _descriptor.searchType === _filter.searchType
      ) {
        return mapDescriptors.current[_idx];
      }
    }
    return null;
  };

  const _getDescriptorByKey = (_key: string): SearchField | null => {
    for (const _idx in mapDescriptors.current) {
      if (mapDescriptors.current[_idx].key === _key) {
        return mapDescriptors.current[_idx];
      }
    }
    return null;
  };

  const _getDescriptorByIdx = (idx: string): SearchField | null => {
    for (const _idx in mapDescriptors.current) {
      if (mapDescriptors.current[_idx]._idx === idx) {
        return mapDescriptors.current[_idx];
      }
    }
    return null;
  };

  const _getDefaultApplyFilter = (idx: string): FilterParam => {
    const descriptor = _getDescriptorByIdx(idx);
    if (descriptor && descriptor.categoryId && descriptor.key && descriptor.filterType && descriptor.searchType) {
      return {
        categoryId: descriptor.categoryId as string,
        filterKey: descriptor.key,
        filterType: descriptor.filterType,
        searchType: descriptor.searchType as string,
        notFilter: false,
      };
    }
    return {} as FilterParam;
  };

  const _sortByArray = (descArray: string[], conditionArr: string[]): string[] => {
    const arr = Array.from(descArray);
    const result: string[] = [];
    for (let i = 0; i < conditionArr.length; i++) {
      const index = arr.indexOf(conditionArr[i]);
      if (index !== -1) {
        result.push(conditionArr[i]);
        arr.splice(index, 1);
      }
    }
    return [...result, ...arr];
  };

  const loadDescriptors = () => {
    for (let i = 0, len = data.descriptors.length; i < len; i++) {
      const _category = data.descriptors[i];
      const _searchFields = _category.searchFields || [];

      for (let j = 0; j < _searchFields.length; j++) {
        const _idx = "descriptor_" + i + "_" + j;
        const _field = _searchFields[j];

        if (restrictedFilters.indexOf(_field.key) !== -1) {
          _searchFields.splice(j, 1);
          j--;
          continue;
        }
        _field._idx = _idx;
        mapDescriptors.current[_idx] = _field;
      }
    }
    loadAppliedFilter();
  };

  const loadAppliedFilter = () => {
    const _filtersVisible: string[] = [];
    const list = data.appliedFilterSettings || [];

    if (list.length > 0) {
      for (let i = 0, len = list.length; i < len; i++) {
        const _filter = list[i];

        const _descriptor = _getDescriptorByFilter(_filter);
        if (_descriptor) {
          appliedFilterSettings.current[_descriptor._idx] = _filter;
          _filtersVisible.push(_descriptor._idx);
        }
      }
      setFiltersVisible(_filtersVisible);
    }

    if (_filtersVisible.length === 0) {
      if (
        !data.filtersVisibleAtInit ||
        data.filtersVisibleAtInit.length === 0 ||
        Object.keys(mapDescriptors.current).length === 0
      ) {
        return;
      }

      // A fresh view: open the page's initial filters, and give those with a
      // page default (`defaultFilters`) their value, so the first query and
      // "Reset to default" both start from it. A stored view never reaches
      // this branch, so a user's own selection always wins.
      const _filtersVisibleAtInit = data.filtersVisibleAtInit || [];
      const _defaultFilters = data.defaultFilters || {};
      appliedFilterSettings.current = {};
      for (let i = 0, len = _filtersVisibleAtInit.length; i < len; i++) {
        const _key = _filtersVisibleAtInit[i];

        const _descriptor = _getDescriptorByKey(_key);
        if (_descriptor) {
          _filtersVisible.push(_descriptor._idx);
          if (_defaultFilters[_key]) {
            appliedFilterSettings.current[_descriptor._idx] = {
              ..._getDefaultApplyFilter(_descriptor._idx),
              filterValue: _defaultFilters[_key],
            };
          }
        }
      }
      setFiltersVisible(_filtersVisible);
    }
  };

  const loadFilters = (filters = filtersVisible) => {
    const _data: ComponentData[] = [];
    for (let i = 0; i < filters.length; i++) {
      const _key = filters[i];
      appliedFilterSettings.current[_key] = appliedFilterSettings.current[_key]
        ? appliedFilterSettings.current[_key]
        : _getDefaultApplyFilter(_key);

      _data.push({
        descriptor: mapDescriptors.current[_key],
        data: appliedFilterSettings.current[_key],
      });
    }
    setComponents(_data);
    submitFilter();
  };

  useEffect(() => {
    if (filterListRef) {
      filterListRef.current = {
        clearFilter: function () {
          if (filterRef.current) {
            filterRef.current.click();
          }
        },
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    action.current = {
      type: ActionEnum.EMPTY,
      idx: null,
    };
    loadDescriptors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    loadFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersVisible]);

  useEffect(() => {
    if (!forceFilterAction) return;

    if (forceFilterAction.action === "CLEAR_FILTER") {
      const targetKey = forceFilterAction.filterKeys[0];
      for (const idxKey in appliedFilterSettings.current) {
        if (appliedFilterSettings.current[idxKey].filterKey === targetKey) {
          delete appliedFilterSettings.current[idxKey].filterValue;
          appliedFilterSettings.current[idxKey].notFilter = false;
          break;
        }
      }

      if (setForceFilterAction) {
        setForceFilterAction(null);
      }

      const _clearedKeys: string[] = [];
      for (const key in appliedFilterSettings.current) {
        _clearedKeys.push(key);
      }

      action.current = { type: ActionEnum.UPDATE_FILTER, idx: null };
      setFiltersVisible(_sortByArray(_clearedKeys, filtersVisible));
      return;
    }

    let existedFilter: FilterParam | null = null;
    for (const key in appliedFilterSettings.current) {
      if (
        appliedFilterSettings.current[key].filterKey ===
        forceFilterAction.filterKeys[0]
      ) {
        existedFilter = appliedFilterSettings.current[key];
      }
    }

    if (!existedFilter) {
      for (const key in mapDescriptors.current) {
        if (
          mapDescriptors.current[key].key === forceFilterAction.filterKeys[0]
        ) {
          appliedFilterSettings.current[key] = _getDefaultApplyFilter(key);
          existedFilter = appliedFilterSettings.current[key];
          break;
        }
      }
    }

    if (existedFilter) {
      existedFilter.filterValue = existedFilter.filterValue
        ? { ...existedFilter.filterValue }
        : {};
      const { filterValue, filterType } = existedFilter;
      const { selectedValues = [], operator } = (filterValue || {}) as { selectedValues?: (string | number)[]; operator?: string };
      const { action: filterAction, query } = forceFilterAction;

      if (filterAction === "FILTER_FOR_VALUE") {
        if (filterType === "MULTI_STRING") {
          filterValue.searchText = query?.length ? String(query[0] || "") : "";
        } else if (filterType === "STRING") {
          if (query?.length) {
            filterValue.searchText = String(query[0] || "");
          } else {
            filterValue.searchText = "";
            filterValue.includeEmpty = true;
            filterValue.includeNoEmpty = false;
          }
        } else if (filterType === "SPATIAL") {
          if (!query?.length) {
            filterValue.includeEmpty = true;
            filterValue.includeNoEmpty = false;
          }
        } else if (filterType === "BOOLEAN") {
          if (query?.length) {
            filterValue.naOnly = false;
            filterValue.value = query[0] === "true" || query[0] === true;
          } else {
            filterValue.value = null;
            filterValue.naOnly = true;
          }
        } else if (filterType === "NUMERIC") {
          existedFilter.notFilter = false;
          const bucketRange = query?.length && typeof query[0] === "object" && query[0] !== null && (query[0] as BucketRange).__bucketRange ? query[0] as BucketRange : null;
          if (bucketRange) {
            if (bucketRange.min != null && bucketRange.max != null) {
              filterValue.gt = bucketRange.min;
              filterValue.lt = bucketRange.max;
              filterValue.operator = "BETWEEN";
            } else if (bucketRange.min != null) {
              filterValue.gt = bucketRange.min;
              filterValue.operator = "GT";
            } else if (bucketRange.max != null) {
              filterValue.lt = bucketRange.max;
              filterValue.operator = "LT";
            }
          } else if (query?.length) {
            filterValue.eq = query[0];
            filterValue.operator = "EQ";
          } else {
            filterValue.eq = null;
            filterValue.operator = "NULL";
          }
        } else if (filterType === "DATE") {
          existedFilter.notFilter = false;

          filterValue.intervalSign = null;
          filterValue.lastUnit = null;
          filterValue.lastValue = null;
          filterValue.relativeEndDate = null;
          filterValue.relativeStartDate = null;
          filterValue.absoluteStartDate = null;
          filterValue.absoluteEndDate = null;
          const dateBucketRange = query?.length && typeof query[0] === "object" && query[0] !== null && (query[0] as BucketRange).__bucketRange ? query[0] as BucketRange : null;
          if (dateBucketRange) {
            filterValue.absoluteStartDate = momentTZ(dateBucketRange.min as string | number | Date)
              .startOf("day")
              .format();
            filterValue.absoluteEndDate = momentTZ(dateBucketRange.max as string | number | Date)
              .endOf("day")
              .format();
            filterValue.dateFilterType = "BETWEEN_ABSOLUTE";
          } else if (query?.length) {
            filterValue.absoluteStartDate = momentTZ(query[0] as string | number | Date)
              .startOf("day")
              .format();
            filterValue.absoluteEndDate = momentTZ(query[0] as string | number | Date)
              .endOf("day")
              .format();
            filterValue.dateFilterType = "BETWEEN_ABSOLUTE";
          } else {
            filterValue.dateFilterType = "NULL";
          }
        } else if (filterType === "MULTI_SELECT_CHECKBOX") {
          if (!query) {
            filterValue.operator = "NULL";
            filterValue.selectedValues = [];
          } else {
            filterValue.operator = "IN";
            filterValue.selectedValues = query || [];
          }
        } else if (filterType === "MULTI_SELECT_AUTO_COMPLETE") {
          existedFilter.notFilter = false;
          if (query?.length) {
            filterValue.includeEmpty = false;
            filterValue.selectedValues = [...query];
          } else {
            filterValue.includeEmpty = true;
            filterValue.selectedValues = [];
          }
        } else {
          filterValue.selectedValues = query || [];
        }
      } else if (filterAction === "FILTER_OUT_VALUE") {
        if (filterType === "MULTI_SELECT_CHECKBOX") {
          if (!query) {
            filterValue.operator = "NOT_NULL";
            filterValue.selectedValues = [];
          } else if (selectedValues.length === 0) {
            filterValue.operator = "NOT_IN";
            filterValue.selectedValues = [...query];
          } else if (operator === "NOT_IN") {
            filterValue.selectedValues = [
              ...new Set([...selectedValues, ...query]),
            ];
          } else if (!operator || operator === "IN") {
            filterValue.selectedValues = (filterValue.selectedValues as (string | number)[]).filter(
              (item) => !forceFilterAction.query?.includes(item)
            );
            if ((filterValue.selectedValues as (string | number)[]).length === 0) {
              filterValue.operator = "NOT_IN";
              filterValue.selectedValues = [...query];
            }
          } else {
            filterValue.operator = "NOT_IN";
            filterValue.selectedValues = [...query];
          }
        } else if (filterType === "MULTI_SELECT_AUTO_COMPLETE") {
          if (query && query.length > 0) {
            if (selectedValues.length === 0) {
              existedFilter.notFilter = true;
              filterValue.selectedValues = [...query];
            } else if (existedFilter.notFilter) {
              filterValue.selectedValues = [
                ...new Set([...selectedValues, ...query]),
              ];
            } else {
              filterValue.selectedValues = (filterValue.selectedValues as (string | number)[]).filter(
                (item) => !query.includes(item)
              );
              if ((filterValue.selectedValues as (string | number)[]).length === 0) {
                existedFilter.notFilter = true;
                filterValue.selectedValues = [...query];
              }
            }
          } else {
            existedFilter.notFilter = true;
            filterValue.includeEmpty = true;
            filterValue.selectedValues = [];
          }
        } else if (filterType === "STRING") {
          if (query && query.length > 0) {
            existedFilter.notFilter = true;
            filterValue.includeEmpty = false;
            filterValue.includeNoEmpty = false;
            filterValue.searchText = String(query[0]);
          } else {
            existedFilter.notFilter = false;
            filterValue.includeEmpty = false;
            filterValue.includeNoEmpty = true;
            filterValue.searchText = "";
          }
        } else if (filterType === "SPATIAL") {
          if (query && query.length > 0) {
            existedFilter.notFilter = true;
            filterValue.includeEmpty = false;
            filterValue.includeNoEmpty = false;
          } else {
            existedFilter.notFilter = false;
            filterValue.includeEmpty = false;
            filterValue.includeNoEmpty = true;
          }
        } else if (filterType === "BOOLEAN") {
          existedFilter.notFilter = true;
          if (query?.length) {
            filterValue.naOnly = false;
            filterValue.value = query[0] === "true" || query[0] === true;
          } else {
            filterValue.value = null;
            filterValue.naOnly = true;
          }
        } else if (filterType === "NUMERIC") {
          existedFilter.notFilter = true;
          const bucketRangeOut = query?.length && typeof query[0] === "object" && query[0] !== null && (query[0] as BucketRange).__bucketRange ? query[0] as BucketRange : null;
          if (bucketRangeOut) {
            if (bucketRangeOut.min != null && bucketRangeOut.max != null) {
              filterValue.gt = bucketRangeOut.min;
              filterValue.lt = bucketRangeOut.max;
              filterValue.operator = "BETWEEN";
            } else if (bucketRangeOut.min != null) {
              filterValue.gt = bucketRangeOut.min;
              filterValue.operator = "GT";
            } else if (bucketRangeOut.max != null) {
              filterValue.lt = bucketRangeOut.max;
              filterValue.operator = "LT";
            }
          } else if (query?.length) {
            filterValue.eq = query[0];
            filterValue.operator = "EQ";
          } else {
            filterValue.eq = null;
            filterValue.operator = "NULL";
          }
        } else if (filterType === "DATE") {
          existedFilter.notFilter = true;
          filterValue.intervalSign = null;
          filterValue.lastUnit = null;
          filterValue.lastValue = null;
          filterValue.relativeEndDate = null;
          filterValue.relativeStartDate = null;
          filterValue.absoluteStartDate = null;
          filterValue.absoluteEndDate = null;
          const dateBucketRangeOut = query?.length && typeof query[0] === "object" && query[0] !== null && (query[0] as BucketRange).__bucketRange ? query[0] as BucketRange : null;
          if (dateBucketRangeOut) {
            filterValue.absoluteStartDate = momentTZ(dateBucketRangeOut.min as string | number | Date)
              .startOf("day")
              .format();
            filterValue.absoluteEndDate = momentTZ(dateBucketRangeOut.max as string | number | Date)
              .endOf("day")
              .format();
            filterValue.dateFilterType = "BETWEEN_ABSOLUTE";
          } else if (query?.length) {
            filterValue.absoluteStartDate = momentTZ(query[0] as string | number | Date)
              .startOf("day")
              .format();
            filterValue.absoluteEndDate = momentTZ(query[0] as string | number | Date)
              .endOf("day")
              .format();
            filterValue.dateFilterType = "BETWEEN_ABSOLUTE";
          } else {
            filterValue.dateFilterType = "NULL";
          }
        }
      }
    }

    if (setForceFilterAction) {
      setForceFilterAction(null);
    }

    const _data: string[] = [];
    for (const key in appliedFilterSettings.current) {
      _data.push(key);
    }

    action.current = { type: ActionEnum.UPDATE_FILTER, idx: null };
    setFiltersVisible(_sortByArray(_data, filtersVisible));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceFilterAction]);

  const onApplyConfigureModal = (
    visibleIdxs: string[],
    draftSettings: Record<string, FilterParam>
  ) => {
    const newApplyFilterSettings: Record<string, FilterParam> = {};
    for (const idx of visibleIdxs) {
      newApplyFilterSettings[idx] =
        draftSettings[idx] || _getDefaultApplyFilter(idx);
    }

    let hasLocationByBound = false;
    for (const key in newApplyFilterSettings) {
      if (newApplyFilterSettings[key].filterKey === "locationByBound") {
        hasLocationByBound = true;
      }
    }
    if (!hasLocationByBound) {
      const event = new CustomEvent("bound_switch_react_change", {
        detail: { switchOn: false },
      });
      document.dispatchEvent(event);
    }

    appliedFilterSettings.current = newApplyFilterSettings;
    action.current = { type: ActionEnum.CHANGE_MORE_FILTER, idx: null };
    setFiltersVisible(visibleIdxs);
  };

  const submitFilter = (settings = appliedFilterSettings.current) => {
    const list: FilterParam[] = [];

    for (let i = 0; i < filtersVisible.length; i++) {
      if (settings[filtersVisible[i]]) {
        list.push(settings[filtersVisible[i]]);
      } else {
        list.push(_getDefaultApplyFilter(filtersVisible[i]));
      }
    }
    if (list.length > 0) {
      const basicQuery: BasicQuery = {
        searchQueryType: "BASIC",
        query: list,
      };

      // Track filter usage: which specific filter was changed
      if (action.current.type !== ActionEnum.EMPTY && lastChangedFilter.current) {
        usageAnalyticsClient.trackCustomEvent("filter-applied", {
          action: action.current.type,
          filter: lastChangedFilter.current,
        });
        lastChangedFilter.current = null;
      }

      if (onChange) {
        onChange(basicQuery, action.current);
      }
    }
  };

  const componentsRender = useMemo(() => {
    const onUpdateSearchFilterItem = (_data: unknown) => {
      if (typeof _data === 'object' && _data !== null && 'data' in _data && (_data as { data: unknown }).data === null) {
        const idxData = _data as { idx: string; data: null };
        const descriptor = _getDescriptorByIdx(idxData.idx);
        lastChangedFilter.current = descriptor?.key ?? null;
        delete appliedFilterSettings.current[idxData.idx];
      } else {
        const _descriptor = _getDescriptorByFilter(_data as FilterParam);
        if (_descriptor) {
          lastChangedFilter.current = _descriptor.key;
          appliedFilterSettings.current[_descriptor._idx] = _data as FilterParam;
        }
      }
      action.current = { type: ActionEnum.UPDATE_FILTER, idx: null };
      loadFilters();
    };

    if (action.current.type === ActionEnum.REMOVE_FILTER) {
      const _render = previousComponentRender.current.filter(
        (c) => c.key !== action.current.idx
      );
      previousComponentRender.current = _render;
      action.current = { type: ActionEnum.EMPTY, idx: null };
      return _render;
    }

    const render: JSX.Element[] = [];
    let locationByBoundComponent: JSX.Element | null = null;

    components.forEach((obj) => {
      const { descriptor, data } = obj;

      let component: JSX.Element | null = null;

      if (descriptor.filterType === "MULTI_STRING") {
        component = (
          <MultiStringFilter
            key={descriptor._idx}
            descriptor={descriptor as any} // eslint-disable-line @typescript-eslint/no-explicit-any
            data={data as any} // eslint-disable-line @typescript-eslint/no-explicit-any
            disabled={disabled}
            onChange={onUpdateSearchFilterItem as any} // eslint-disable-line @typescript-eslint/no-explicit-any
          />
        );
      } else if (descriptor.filterType === "STRING") {
        component = (
          <StringFilter
            key={descriptor._idx}
            descriptor={descriptor as any} // eslint-disable-line @typescript-eslint/no-explicit-any
            data={data as any} // eslint-disable-line @typescript-eslint/no-explicit-any
            disabled={disabled}
            onChange={onUpdateSearchFilterItem as any} // eslint-disable-line @typescript-eslint/no-explicit-any
          />
        );
      } else if (descriptor.filterType === "SPATIAL") {
        component = (
          <BoundFilter
            key={descriptor._idx}
            descriptor={descriptor as any} // eslint-disable-line @typescript-eslint/no-explicit-any
            data={data as any} // eslint-disable-line @typescript-eslint/no-explicit-any
            disabled={disabled}
            onChange={onUpdateSearchFilterItem as any} // eslint-disable-line @typescript-eslint/no-explicit-any
          />
        );
      } else if (descriptor.filterType === "DATE") {
        component = (
          <DateFilter
            key={descriptor._idx}
            descriptor={descriptor as any} // eslint-disable-line @typescript-eslint/no-explicit-any
            data={data as any} // eslint-disable-line @typescript-eslint/no-explicit-any
            disabled={disabled}
            onChange={onUpdateSearchFilterItem as any} // eslint-disable-line @typescript-eslint/no-explicit-any
          />
        );
      } else if (descriptor.filterType === "NUMERIC") {
        component = (
          <NumericFilter
            key={descriptor._idx}
            descriptor={descriptor as any} // eslint-disable-line @typescript-eslint/no-explicit-any
            data={data as any} // eslint-disable-line @typescript-eslint/no-explicit-any
            disabled={disabled}
            onChange={onUpdateSearchFilterItem as any} // eslint-disable-line @typescript-eslint/no-explicit-any
          />
        );
      } else if (descriptor.filterType === "BOOLEAN") {
        component = (
          <BooleanFilter
            key={descriptor._idx}
            descriptor={descriptor as any} // eslint-disable-line @typescript-eslint/no-explicit-any
            data={data as any} // eslint-disable-line @typescript-eslint/no-explicit-any
            disabled={disabled}
            onChange={onUpdateSearchFilterItem as any} // eslint-disable-line @typescript-eslint/no-explicit-any
          />
        );
      } else if (descriptor.filterType === "MULTI_SELECT_CHECKBOX") {
        component = (
          <MultiCheckboxFilter
            key={descriptor._idx}
            descriptor={descriptor as any} // eslint-disable-line @typescript-eslint/no-explicit-any
            data={data as any} // eslint-disable-line @typescript-eslint/no-explicit-any
            disabled={disabled}
            onChange={onUpdateSearchFilterItem as any} // eslint-disable-line @typescript-eslint/no-explicit-any
            deviceCategorySuggestion={deviceCategorySuggestion}
            orgId={orgId}
          />
        );
      } else if (descriptor.filterType === "MULTI_SELECT_AUTO_COMPLETE") {
        component = (
          <MultiSelectAutocompleteFilter
            key={descriptor._idx}
            descriptor={descriptor as any} // eslint-disable-line @typescript-eslint/no-explicit-any
            data={data as any} // eslint-disable-line @typescript-eslint/no-explicit-any
            disabled={disabled}
            onChange={onUpdateSearchFilterItem as any} // eslint-disable-line @typescript-eslint/no-explicit-any
            orgId={orgId}
          />
        );
      }

      if (descriptor.key === "locationByBound") {
        locationByBoundComponent = component;
      } else if (component) {
        render.push(component);
      }
    });

    if (locationByBoundComponent) {
      render.push(locationByBoundComponent);
    }

    previousComponentRender.current = render;
    return render;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [components, disabled, onChange, orgId]);

  return (
    <>
      <SntCollapse isDisableCollapseOnMobile={isDisableCollapseOnMobile}>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div style={{ float: "left" }}>
            {componentsRender}
            {isVisibleAddFilterBtn && Object.keys(mapDescriptors.current).length > 1 && (
              <>
                <AdvanceFilter>
                  <Button
                    variant="sensolus-greylight"
                    title={translate(LanguageKey.all_filter_description_key)}
                    disabled={disabled}
                    onClick={() => setModalShow(true)}
                    className="d-inline-flex align-items-center gap-1"
                  >
                    <SntFilterSettingIcon size={16} />
                    {translate(LanguageKey.configure_filters_key)}
                  </Button>
                </AdvanceFilter>
                <ConfigureFiltersModal
                  isShow={isModalShow}
                  descriptors={data.descriptors as any} // eslint-disable-line @typescript-eslint/no-explicit-any
                  visibleIdxs={filtersVisible}
                  defaultVisibleIdxs={(data.filtersVisibleAtInit || []).map((key) => _getDescriptorByKey(key)?._idx).filter(Boolean) as string[]}
                  appliedFilterSettings={appliedFilterSettings.current as any} // eslint-disable-line @typescript-eslint/no-explicit-any
                  onApply={onApplyConfigureModal as any} // eslint-disable-line @typescript-eslint/no-explicit-any
                  onClose={() => setModalShow(false)}
                  deviceCategorySuggestion={deviceCategorySuggestion}
                  disabled={disabled}
                  orgId={orgId}
                />
              </>
            )}
            {totalElement && (
              <AdvanceFilter className="text-sensolus">
                {totalElement}
              </AdvanceFilter>
            )}
            {onResetFilterData && !disabled ? (
              <AdvanceFilter>
                <SntLink
                  ref={filterRef}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onResetFilterData();
                  }}
                >
                  {translate(LanguageKey.clear_all)}
                </SntLink>
              </AdvanceFilter>
            ) : null}
          </div>
          {filterToolList && filterToolList.length > 0 && (
            <div
              style={{
                overflow: "hidden",
                float: "none",
                flex: "1 1 calc(20%)",
              }}
            >
              <AdvanceFilter className="w-100">
                <ActionTool
                  list={filterToolList}
                  onClick={handleTool}
                  group={undefined}
                />
              </AdvanceFilter>
            </div>
          )}
        </div>
      </SntCollapse>
    </>
  );
};

export default FilterList;
